import React, { useState, useEffect } from "react";
import { Plus, AlertTriangle, Check, X, Pencil, Trash2 } from "lucide-react";
import PageLayout from "../../components/PageLayout";
import Modal from "../../components/Modal";
import ConfirmDialog from "../../components/ConfirmDialog";
import { useAuth } from "../../context/AuthContext";
import { violations as api, type Violation, type ApiError } from "../../services/api";
import DataGrid from "../../components/DataGrid";

const inp = {
  width: "100%",
  padding: "9px 12px",
  background: "var(--nx-surface-3)",
  border: "1px solid var(--nx-border)",
  borderRadius: 8,
  fontSize: 13,
  color: "var(--nx-text-primary)",
  outline: "none",
  boxSizing: "border-box" as const,
};
const lbl = {
  display: "block",
  fontSize: 11,
  fontWeight: 600,
  color: "var(--nx-text-secondary)",
  marginBottom: 5,
  letterSpacing: "0.05em",
  textTransform: "uppercase" as const,
};

const INIT = { category: "", description: "" };

const statusColor: Record<string, string> = {
  OPEN: "#F59E0B",
  RESOLVED: "#22C55E",
  DISMISSED: "#8B93B3",
};

const ViolationsPage: React.FC = () => {
  const { user } = useAuth();
  const societyId = user?.societyId ?? null;
  const isAdmin = user?.role !== "RESIDENT" && user?.role !== "TENANT";

  const [items, setItems] = useState<Violation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Violation | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [form, setForm] = useState(INIT);
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Violation | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!societyId) { setLoading(false); return; }
    api.list(societyId).then(setItems).catch(() => {}).finally(() => setLoading(false));
  }, [societyId]);

  const canModify = (v: Violation) =>
    isAdmin || (v.reportedBy === user?.id && v.status === "OPEN");

  const openAdd = () => { setEditing(null); setFormError(""); setForm(INIT); setShowForm(true); };
  const openEdit = (v: Violation) => {
    setEditing(v);
    setFormError("");
    setForm({ category: v.category, description: v.description ?? "" });
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditing(null); setForm(INIT); setFormError(""); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId) return;
    setSaving(true); setFormError("");
    try {
      if (editing) {
        const updated = await api.update(societyId, editing.id, { category: form.category, description: form.description || undefined });
        setItems(p => p.map(x => x.id === editing.id ? updated : x));
      } else {
        const created = await api.create(societyId, { category: form.category, description: form.description || undefined });
        setItems(p => [created, ...p]);
      }
      closeForm();
    } catch (err) {
      setFormError((err as ApiError).message || "Failed to save violation.");
    } finally { setSaving(false); }
  };

  const handleResolve = async (v: Violation, status: "RESOLVED" | "DISMISSED") => {
    if (!societyId) return;
    setResolvingId(v.id);
    try {
      const updated = await api.resolve(societyId, v.id, status);
      setItems(p => p.map(x => x.id === v.id ? updated : x));
    } catch { /* ignore */ }
    finally { setResolvingId(null); }
  };

  const confirmDelete = async () => {
    if (!societyId || !deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(societyId, deleteTarget.id);
      setItems(p => p.filter(x => x.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch { /* ignore */ }
    finally { setDeleting(false); }
  };

  return (
    <PageLayout>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: "Syne, sans-serif", fontSize: 22, fontWeight: 700, color: "var(--nx-text-primary)", letterSpacing: "-0.03em", marginBottom: 4 }}>
            Violations
          </h1>
          <p style={{ fontSize: 13, color: "var(--nx-text-muted)" }}>
            {items.length} reported society rule violations
          </p>
        </div>
        <button onClick={openAdd} className="nx-btn nx-btn-primary" style={{ gap: 6 }}>
          <Plus size={14} /> Report Violation
        </button>
      </div>

      <div className="nx-card" style={{ padding: 0, overflow: "hidden" }}>
        <DataGrid
          columns={[
            { key: "category", header: "Category", cellStyle: { fontWeight: 500 }, render: (v: Violation) => v.category },
            { key: "description", header: "Description", cellStyle: { color: "var(--nx-text-secondary)" }, render: (v: Violation) => v.description || "—" },
            {
              key: "status", header: "Status",
              render: (v: Violation) => (
                <span style={{ fontSize: 11, fontWeight: 700, color: statusColor[v.status], textTransform: "uppercase" }}>{v.status}</span>
              ),
            },
            {
              key: "actions", header: "",
              cellStyle: { textAlign: "right" as const, whiteSpace: "nowrap" as const },
              render: (v: Violation) => (
                <span style={{ display: "inline-flex", gap: 6 }}>
                  {isAdmin && v.status === "OPEN" && (
                    <>
                      <button onClick={() => handleResolve(v, "RESOLVED")} disabled={resolvingId === v.id}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#22C55E" }} title="Resolve">
                        <Check size={14} />
                      </button>
                      <button onClick={() => handleResolve(v, "DISMISSED")} disabled={resolvingId === v.id}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#8B93B3" }} title="Dismiss">
                        <X size={14} />
                      </button>
                    </>
                  )}
                  {canModify(v) && (
                    <>
                      <button onClick={() => openEdit(v)}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "var(--nx-text-muted)" }} title="Edit">
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => setDeleteTarget(v)}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#EF4444" }} title="Delete">
                        <Trash2 size={14} />
                      </button>
                    </>
                  )}
                </span>
              ),
            },
          ]}
          rows={items}
          rowKey={v => v.id}
          loading={loading}
          emptyIcon={<AlertTriangle size={32} style={{ color: "var(--nx-text-muted)" }} />}
          emptyText="No violations reported."
        />
      </div>

      {showForm && (
        <Modal
          title={editing ? "Edit Violation" : "Report a Violation"}
          submitLabel={editing ? "Save Changes" : "Report"}
          error={formError}
          isLoading={saving}
          onClose={closeForm}
          onSubmit={handleSubmit}
        >
          <div>
            <label style={lbl}>Category *</label>
            <input value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} required placeholder="e.g. Noise, Parking, Pets" style={inp} autoFocus />
          </div>
          <div>
            <label style={lbl}>Description</label>
            <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Details" style={{ ...inp, minHeight: 80, resize: "vertical" as const }} />
          </div>
        </Modal>
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        variant="danger"
        title={`Delete this ${deleteTarget?.category ?? ""} violation report?`}
        message="This violation report will be permanently removed. This action cannot be undone."
        confirmLabel="Delete Report"
        isLoading={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </PageLayout>
  );
};

export default ViolationsPage;
